import * as React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { GTM_DATA_ATTRIBUTE } from '#/shared/constants';

const getButtonStyle = (props, property = null) => {
  let styles = {};

  switch (props.variant) {
    case 'primary':
      styles = {
        backgroundColor: props.theme.primaryButtonBg,
        backgroundColorHover: props.theme.primaryButtonBgHover,
        borderColor: props.theme.primaryButtonBorder,
        borderColorHover: props.theme.primaryButtonBorderHover,
        textColor: props.theme.primaryButtonText,
        textColorHover: props.theme.primaryButtonText,
        textShadow: props.theme.primaryButtonTextShadow,
      };
      break;
    case 'outline':
      styles = {
        backgroundColor: props.theme.outlineButtonBg,
        backgroundColorHover: props.theme.outlineButtonBgHover,
        borderColor: props.theme.outlineButtonBorder,
        borderColorHover: props.theme.outlineButtonBorderHover,
        textColor: props.theme.outlineButtonText,
        textColorHover: props.theme.outlineButtonTextHover,
        textShadow: 'none',
      };
      break;
    case 'outline-reverse':
      styles = {
        backgroundColor: props.theme.outlineReverseButtonBg,
        backgroundColorHover: props.theme.outlineReverseButtonBgHover,
        borderColor: props.theme.outlineReverseButtonBorder,
        borderColorHover: props.theme.outlineReverseButtonBorderHover,
        textColor: props.theme.outlineReverseButtonText,
        textColorHover: props.theme.outlineReverseButtonTextHover,
        textShadow: 'none',
      };
      break;
    default:
      styles = {
        backgroundColor: 'transparent',
        backgroundColorHover: 'transparent',
        borderColor: 'transparent',
        borderColorHover: 'transparent',
        textColor: 'transparent',
        textColorHover: 'transparent',
        textShadow: 'none',
      };
  }

  return property ? styles[property] : styles;
};

const ButtonBase = styled.button.attrs(props => ({
  [GTM_DATA_ATTRIBUTE]: props.dataGtm,
}))`
  display: ${props => (props.active ? 'inline-flex' : 'none')};
  align-items: center;
  background-color: ${props => getButtonStyle(props, 'backgroundColor')};
  border: 1px solid ${props => getButtonStyle(props, 'borderColor')};
  border-radius: ${props =>
    props.rounded ? props.theme.buttonRounded : props.theme.buttonRadius};
  padding: 8px 12px;
  color: ${props => getButtonStyle(props, 'textColor')};
  font-size: 1.2em;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  text-shadow: ${props => getButtonStyle(props, 'textShadow')};

  @media (any-hover: hover) {
    &:hover {
      cursor: ${props => (props.active ? 'pointer' : 'default')};
      background-color: ${props =>
        getButtonStyle(props, 'backgroundColorHover')};
      color: ${props => getButtonStyle(props, 'textColorHover')};
      box-shadow: ${props => props.theme.buttonShadowHover};
      border: 1px solid ${props => getButtonStyle(props, 'borderColorHover')};
    }
  }

  & > svg {
    fill: ${props => getButtonStyle(props, 'textColor')};
  }
  &:hover > svg {
    fill: ${props => getButtonStyle(props, 'textColorHover')};
  }

  &:focus,
  &:active {
    background-color: ${props => getButtonStyle(props, 'backgroundColorHover')};
    color: ${props => getButtonStyle(props, 'textColorHover')};
  }
  &:focus svg {
    fill: ${props => getButtonStyle(props, 'textColorHover')};
  }
`;

function Button({
  active,
  variant,
  dataGtm,
  children,
  rounded,
  className,
  ...rest
}) {
  return (
    <ButtonBase
      active={active}
      variant={variant}
      rounded={rounded}
      dataGtm={dataGtm}
      className={className}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </ButtonBase>
  );
}

Button.propTypes = {
  /** Determines whether to display the button or not */
  active: PropTypes.bool,
  /** Determines the styling applied to the button */
  variant: PropTypes.string.isRequired,
  /** The dataGtm handle  */
  dataGtm: PropTypes.string,
  /** className so we can add styles to them */
  className: PropTypes.string,
  /** If the button should be rounded or not */
  rounded: PropTypes.bool,
  /** The children */
  children: PropTypes.node,
};

Button.defaultProps = {
  active: true,
  dataGtm: '',
  className: '',
  rounded: false,
  children: null,
};

export default Button;
